import { useParentChild } from "@/contexts/ParentChildContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Users2, ChevronDown, Check } from "lucide-react";

const initials = (n) => (n || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

/**
 * Compact global child switcher shown in the top bar for parents with 2+ children.
 * Renders nothing when the user is not a parent or has 0-1 children.
 */
export default function ParentChildSwitcher() {
  const { isParent, hasMultiple, children, activeChildId, setActiveChildId, activeChild } = useParentChild();
  if (!isParent || !hasMultiple) return null;
  const label = activeChildId === "all" ? "Tümü" : (activeChild?.name?.split(" ")[0] || "—");
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-9 gap-2 rounded-full border-indigo-500/30 bg-indigo-500/5 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-500/10"
          data-testid="parent-child-switcher"
          aria-label="Aktif çocuk seçici"
        >
          {activeChildId === "all"
            ? <Users2 size={14} />
            : <span className="w-5 h-5 rounded-md bg-indigo-500 text-white text-[10px] font-bold grid place-items-center">{initials(activeChild?.name)}</span>}
          <span className="hidden sm:inline text-sm font-medium">{label}</span>
          <span className="text-[10px] text-muted-foreground">({children.length})</span>
          <ChevronDown size={12} aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56" data-testid="parent-child-menu">
        <DropdownMenuLabel className="text-[11px] uppercase tracking-widest text-muted-foreground">Aktif Çocuk</DropdownMenuLabel>
        <DropdownMenuItem
          onClick={() => setActiveChildId("all")}
          data-testid="switcher-child-all"
          className="cursor-pointer"
        >
          <Users2 size={14} className="mr-2" /> Tümü <span className="ml-auto text-[10px] text-muted-foreground">({children.length})</span>
          {activeChildId === "all" && <Check size={12} className="ml-2 text-indigo-500" />}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {children.map((c) => (
          <DropdownMenuItem
            key={c.id}
            onClick={() => setActiveChildId(c.id)}
            data-testid={`switcher-child-${c.id}`}
            className="cursor-pointer"
          >
            <span className="w-5 h-5 rounded-md bg-orange-500/15 text-orange-600 text-[10px] font-bold grid place-items-center mr-2">{initials(c.name)}</span>
            <span className="flex-1 truncate">{c.name}</span>
            {activeChildId === c.id && <Check size={12} className="ml-2 text-indigo-500" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
